import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { supabaseReportException } from "@/lib/logs/reportExceptions";
import { hasAccountRole } from "@/lib/accountRoles";
import type { AccountRole } from "@/lib/accountRoles";
import { useAuth } from "./useAuth";

export const useAccountRoles = () => {
  const { user, loading: authLoading } = useAuth();
  const roleQuery = useQuery({
    queryKey: ["account-roles", user?.id],
    enabled: !authLoading && Boolean(user),
    queryFn: async (): Promise<AccountRole[]> => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("account_roles")
        .select("role")
        .eq("user_id", user.id)
        .order("role", { ascending: true });

      if (error) {
        supabaseReportException(error, {
          operation: "useAccountRoles.supabaseQueryError",
          additionalData: { userId: user.id },
        });
        throw error;
      }

      return data.map(({ role }) => role);
    },
  });

  const roles = useMemo(
    () => (user && !roleQuery.error ? (roleQuery.data ?? []) : []),
    [roleQuery.data, roleQuery.error, user],
  );
  const hasRole = useCallback(
    (role: AccountRole) => hasAccountRole(roles, role),
    [roles],
  );

  return {
    roles,
    isCustomer: hasRole("customer"),
    isExpert: hasRole("expert"),
    hasRole,
    loading: authLoading || (Boolean(user) && roleQuery.isLoading),
    error: roleQuery.error,
    refresh: roleQuery.refetch,
  };
};
